// Full column set for the IM "Item/Lot" grid — every uIMM0034.qryIMLot field the GetItemLots projection
// returns, with the Delphi captions ("… - Item" / "… - Lot"). The 5 screenshot columns are visible; the rest
// are hidden (column chooser). dataFields are camelCase (ItemManagementController serialization).
// Legacy Price Level A-E and User Calculated 1-10 are SQL scalar-function columns and are intentionally omitted.
import { LotStatus } from "../../../const/Lookup";

const money = { type: "fixedPoint", precision: 2 };
const txt = (dataField, caption, visible = false) => ({ dataField, caption, visible });
const num = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "number", format: money });
const dt = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "date", format: "MM/dd/yyyy" });
const dtm = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "datetime", format: "MM/dd/yyyy hh:mm a" });
// User-defined passthroughs: `${labelPrefix} ${n} - ${scope}` (e.g. "Code 1 - Item", "Quantity 10 - Lot").
const udf = (fieldPrefix, labelPrefix, scope, kind) =>
  Array.from({ length: 10 }, (_, i) => {
    const n = i + 1;
    const field = `${fieldPrefix}${n}`;
    const label = `${labelPrefix} ${n} - ${scope}`;
    return kind === "date" ? dt(field, label) : kind === "num" ? num(field, label) : txt(field, label);
  });

const ITEM_LOT_COLUMNS = [
  // Visible (matches the Delphi saved layout in the screenshot).
  txt("itemCode", "Item", true),
  txt("itemDescription1", "Item Description - 1", true),
  txt("lotCode", "Lot", true),
  txt("lotDescription1", "Lot Description - 1", true),
  num("lotNetAvailable", "Lot Net Available", true),

  // --- Item-level identity/descriptive (hidden) ---
  txt("itemRecordNumber", "Record # - Item"),
  txt("itemDescription2", "Item Description - 2"),
  txt("itemClassCode", "Item Class"),
  txt("itemSubClassCode", "Item Subclass"),
  txt("warehouse", "WH-Item"),
  txt("department", "Department"),
  num("itemAvgCost", "Item Avg Cost"),
  num("itemStdCost", "Item Std Cost"),
  num("itemLastCost", "Item Last Cost"),
  num("saleMaxQuantity", "Sale Max Qty"),
  dt("itemDateLastReceipt", "Last Receipt Date (Item)"),
  dtm("itemCreationDateTime", "Creation Date - Item"),
  txt("itemCreationUserId", "Created By - Item"),
  dtm("itemLastUpdateDateTime", "Last Updated - Item"),
  txt("itemLastUpdateUserId", "Last Updated By - Item"),

  // --- Item-level inventory ---
  num("itemOnHand", "Item On-Hand"),
  num("itemInReceiving", "Item In-Receiving"),
  num("itemInShipping", "Item In-Shipping"),
  num("itemUnavailable", "Item Unavailable"),
  num("itemAvailable", "Item Available"),
  num("itemPendingRec", "Item Pending Rec"),
  num("itemPendingShip", "Item Pending Ship"),
  num("itemBOReceiving", "Item BO-Receiving"),
  num("itemBoShipping", "Item Bo-Shipping"),
  num("itemNetAvailable", "Item Net Available"),
  num("itemFutureRec", "Item Future Rec"),
  num("itemFutureShip", "Item Future Ship"),

  // --- Item-level user-defined ---
  ...udf("itemCode", "Code", "Item", "txt"),
  ...udf("itemReference", "Reference", "Item", "txt"),
  ...udf("itemDate", "Date", "Item", "date"),
  ...udf("itemQuantity", "Quantity", "Item", "num"),

  // --- Lot-level identity/descriptive ---
  txt("recordNumber", "Record # - Lot"),
  txt("lotDescription2", "Lot Description - 2"),
  txt("lotClassCode", "Lot Class"),
  txt("lotSubClassCode", "Lot Subclass"),
  { dataField: "lotStatus", caption: "Lot Status", visible: false, lookup: { dataSource: LotStatus, valueExpr: "key", displayExpr: "value" } },
  num("lotAvgCost", "Lot Avg Cost"),
  num("lotStdCost", "Lot Std Cost"),
  num("lotLastCost", "Lot Last Cost"),
  num("lotPiecesPerUnitQty", "Lot - Pieces/Unit Qty"),
  num("lotUnitsPerPackageQty", "Lot - Units/Package Qty"),
  txt("lotPiecesPerUnitUom", "Lot - Pieces/Unit UOM"),
  txt("lotUnitsPerPackageUom", "Lot - Units/Package UOM"),
  dt("lotIntroducedDate", "Lot Introduced Date"),
  dt("lotAvailableDate", "Lot Available Date"),
  dt("lotExpirationDate", "Lot Expiration Date"),
  num("lotOverSellPercentage", "Lot Oversell Percentage"),
  dt("lotDateLastReceipt", "Last Receipt Date (Lot)"),
  dtm("lotCreationDateTime", "Creation Date"),
  txt("lotCreationUserId", "Created By"),
  dtm("lotLastUpdateDateTime", "Last Updated"),
  txt("lotLastUpdateUserId", "Last Updated By"),

  // --- Lot-level inventory ---
  num("lotOnHand", "Lot On-Hand"),
  num("lotInReceiving", "Lot In-Receiving"),
  num("lotInShipping", "Lot In-Shipping"),
  num("lotUnavailable", "Lot Unavailable"),
  num("lotAvailable", "Lot Available"),
  num("lotPendingRec", "Lot Pending Rec"),
  num("lotPendingShip", "Lot Pending Ship"),
  num("lotBOReceiving", "Lot BO-Receiving"),
  num("lotBoShipping", "Lot BO-Shipping"),
  num("lotFutureRec", "Lot Future Rec"),
  num("lotFutureShip", "Lot Future Ship"),

  // --- Lot-level user-defined ---
  ...udf("lotCode", "Code", "Lot", "txt"),
  ...udf("lotReference", "Reference", "Lot", "txt"),
  ...udf("lotDate", "Date", "Lot", "date"),
  ...udf("lotQuantity", "Quantity", "Lot", "num"),
];

export default ITEM_LOT_COLUMNS;
